import React from "react";
import { Link } from "react-router-dom";
import { toast } from "react-toastify";
import { useAppSelector } from "../../store/hooks";
import { redirectToLogin } from "../../utils/oauth";
import { Button } from "../../ui/components/Button";
import {
  useGetAgentGameCurrentQuery,
  useGetAgentGameHistoryQuery,
  useGetAgentGameResultsQuery,
  useJoinAgentGameMutation,
  useSetAgentGameChoiceMutation,
  useSetAgentGamePromptMutation,
  type AgentGamePublic,
  type AgentGameSlotPublic,
} from "../../services/rtkApi/agentGameApi";
import { useGetAgentInfluenceRoleCardsQuery, type CardDefinition } from "../../services/rtkApi/respectCardsApi";
import RespectCard from "../../features/respectCards/components/RespectCard";

const CHOICE_HEX: Record<string, string> = {
  red: "#e53935",
  blue: "#1e88e5",
  green: "#43a047",
  yellow: "#fdd835",
  purple: "#8e24aa",
  orange: "#fb8c00",
  pink: "#ec407a",
  black: "#212121",
  white: "#eceff1",
};

function choiceStyle(choice: string): React.CSSProperties {
  const key = choice.trim().toLowerCase();
  const bg = CHOICE_HEX[key] || "#64748b";
  return {
    backgroundColor: bg,
    color: key === "yellow" || key === "white" ? "#111" : "#fff",
  };
}

function catalogBySlug(catalog: CardDefinition[] | undefined): Map<string, CardDefinition> {
  const m = new Map<string, CardDefinition>();
  for (const c of catalog || []) {
    const slug = c.slug?.trim();
    if (slug) m.set(slug, c);
  }
  return m;
}

function formatRemaining(endsAt: string): string {
  const end = new Date(endsAt).getTime();
  const now = Date.now();
  const ms = Math.max(0, end - now);
  const h = Math.floor(ms / 3600000);
  const m = Math.floor((ms % 3600000) / 60000);
  return `${h}h ${m}m`;
}

function GameSlotGrid({
  game,
  defs,
}: {
  game: AgentGamePublic;
  defs: Map<string, CardDefinition>;
}) {
  return (
    <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
      {game.config.slots.map((slot) => {
        const def = defs.get(slot.roleSlug) || null;
        return (
          <div
            key={slot.roleSlug}
            className="rounded-xl border border-slate-700/80 bg-slate-900/40 p-3 shadow-inner"
          >
            <div className="mb-2 flex justify-center">
              <RespectCard card={def} size="sm" showDetails={Boolean(def)} expandable={false} />
            </div>
            <div className="text-center text-sm font-semibold text-slate-100">{slot.roleName}</div>
            <div className="mt-1 text-center text-xs text-slate-400">
              {slot.joined ? (
                <span>
                  @{slot.username}
                  {game.status === "completed" || game.status === "archived" ? (
                    <span className="ml-1 text-slate-500">
                      {slot.choice ? `· picked ${slot.choice}` : ""}
                    </span>
                  ) : null}
                </span>
              ) : (
                <span className="text-amber-200/80">Open seat</span>
              )}
            </div>
            <div className="mt-2 min-h-[2.5rem] rounded-md bg-slate-950/50 p-2 text-xs text-slate-300">
              {slot.prompt ? (
                <span className="italic">&ldquo;{slot.prompt}&rdquo;</span>
              ) : (
                <span className="text-slate-500">No prompt yet</span>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
}

export default function AgentGamePage() {
  const user = useAppSelector((s) => s.auth);
  const isAuthenticated = Boolean(user.token);
  const username = user.username || "";

  const { data: currentData, refetch: refetchCurrent } = useGetAgentGameCurrentQuery(undefined, {
    pollingInterval: 20000,
  });
  const { data: historyData } = useGetAgentGameHistoryQuery();
  // Phase 2 cards-launch: agent-influence roles only (`agent_influence`
  // typed cards). Public catalog is locked down.
  const { data: catalogData } = useGetAgentInfluenceRoleCardsQuery();

  const defs = React.useMemo(() => catalogBySlug(catalogData?.cards), [catalogData?.cards]);

  const game = currentData?.game ?? null;
  const lastDone = historyData?.games?.[0] ?? null;

  const resultsId =
    !game && lastDone && (lastDone.status === "completed" || lastDone.status === "archived")
      ? lastDone.id
      : game && (game.status === "completed" || game.status === "archived")
        ? game.id
        : null;

  const { data: resultsData } = useGetAgentGameResultsQuery(resultsId!, {
    skip: !resultsId,
  });

  const [joinGame, { isLoading: joining }] = useJoinAgentGameMutation();
  const [setPrompt, { isLoading: savingPrompt }] = useSetAgentGamePromptMutation();
  const [setChoice, { isLoading: savingChoice }] = useSetAgentGameChoiceMutation();

  const [draftPrompt, setDraftPrompt] = React.useState("");

  const mySlot: AgentGameSlotPublic | undefined = React.useMemo(() => {
    if (!game || !username) return undefined;
    return game.config.slots.find((s) => s.joined && s.username === username);
  }, [game, username]);

  React.useEffect(() => {
    if (mySlot?.prompt != null) setDraftPrompt(mySlot.prompt);
  }, [mySlot?.prompt]);

  const handleJoin = async (roleSlug: string) => {
    if (!isAuthenticated) {
      redirectToLogin();
      return;
    }
    if (!game) return;
    try {
      await joinGame({ gameId: game.id, roleSlug }).unwrap();
      toast.success("You claimed this role");
      refetchCurrent();
    } catch (e: unknown) {
      const msg = e && typeof e === "object" && "data" in e ? String((e as any).data?.message) : "Join failed";
      toast.error(msg);
    }
  };

  const handleSavePrompt = async () => {
    if (!game || !mySlot) return;
    try {
      await setPrompt({ gameId: game.id, prompt: draftPrompt }).unwrap();
      toast.success("Prompt saved");
    } catch (e: unknown) {
      const msg = e && typeof e === "object" && "data" in e ? String((e as any).data?.message) : "Save failed";
      toast.error(msg);
    }
  };

  const handlePickChoice = async (choice: string) => {
    if (!game) return;
    try {
      await setChoice({ gameId: game.id, choice }).unwrap();
      toast.success(`Locked in: ${choice}`);
    } catch (e: unknown) {
      const msg = e && typeof e === "object" && "data" in e ? String((e as any).data?.message) : "Choice failed";
      toast.error(msg);
    }
  };

  return (
    <div className="mx-auto max-w-6xl px-4 py-8 text-slate-100">
      <div className="mb-6 flex flex-col gap-2 sm:flex-row sm:items-end sm:justify-between">
        <div>
          <h1 className="text-2xl font-bold tracking-tight">Agent influence game</h1>
          <p className="mt-1 max-w-2xl text-sm text-slate-400">
            Claim a role card, write a short public prompt, and pick a secret {game?.config.choiceLabel || "choice"}.
            Chat with the agent to sway them — others can see your prompt but not your secret pick. When the game ends,
            the agent declares a winning pick via the platform; players who match split respect (smaller groups earn
            more per person).
          </p>
        </div>
        <Link
          to="/agent-chat"
          className="inline-flex items-center justify-center rounded-lg border border-violet-500/40 bg-violet-950/30 px-4 py-2 text-sm font-medium text-violet-200 hover:bg-violet-900/40"
        >
          Open agent chat
        </Link>
      </div>

      {!game && (
        <div className="mb-8 rounded-lg border border-slate-700 bg-slate-900/30 p-4 text-slate-300">
          <p className="font-medium text-slate-200">No live game right now.</p>
          <p className="mt-1 text-sm text-slate-400">Check back when the next event is announced.</p>
        </div>
      )}

      {game && (
        <div className="mb-8 space-y-4">
          <div className="flex flex-wrap items-center gap-3 rounded-lg border border-slate-700 bg-slate-900/40 px-4 py-3">
            <span className="text-lg font-semibold">{game.name}</span>
            <span className="rounded-full bg-slate-800 px-2 py-0.5 text-xs uppercase tracking-wide text-slate-300">
              {game.status}
            </span>
            {game.status === "active" && (
              <span className="text-sm text-amber-200">Ends in ~{formatRemaining(game.endsAt)}</span>
            )}
            {game.status === "revealing" && (
              <span className="text-sm text-violet-200">Reveal phase — the agent should declare soon</span>
            )}
          </div>
          {game.description ? <p className="text-sm text-slate-400">{game.description}</p> : null}

          <div className="rounded-lg border border-amber-900/40 bg-amber-950/20 p-3 text-sm text-amber-100/90">
            <strong className="text-amber-200">Strategy tip:</strong> your secret {game.config.choiceLabel} is hidden
            from other players. Keeping it quiet may reduce coordinated counter-play.
          </div>

          <GameSlotGrid game={game} defs={defs} />

          {game.status === "active" && (
            <div className="space-y-6 rounded-xl border border-slate-700 bg-slate-950/40 p-4">
              {!isAuthenticated && (
                <p className="text-sm text-slate-400">
                  <Button variant="secondary" type="button" onClick={() => redirectToLogin()}>
                    Log in
                  </Button>{" "}
                  to claim a role and play.
                </p>
              )}

              {isAuthenticated && !mySlot && (
                <div>
                  <h3 className="mb-2 text-sm font-semibold text-slate-200">Claim an open seat</h3>
                  <div className="flex flex-wrap gap-2">
                    {game.config.slots
                      .filter((s) => !s.joined)
                      .map((s) => (
                        <Button
                          key={s.roleSlug}
                          type="button"
                          variant="secondary"
                          haptic="medium"
                          disabled={joining}
                          onClick={() => handleJoin(s.roleSlug)}
                        >
                          Join as {s.roleName}
                        </Button>
                      ))}
                  </div>
                  {game.config.slots.every((s) => s.joined) && (
                    <p className="text-sm text-slate-500">All roles are taken.</p>
                  )}
                </div>
              )}

              {isAuthenticated && mySlot && (
                <>
                  <div>
                    <h3 className="mb-2 text-sm font-semibold text-slate-200">Your public prompt</h3>
                    <p className="mb-2 text-xs text-slate-500">
                      Max {game.config.maxPromptLength} characters — visible to everyone.
                    </p>
                    <textarea
                      className="w-full rounded-md border border-slate-600 bg-slate-950 px-3 py-2 text-sm text-slate-100"
                      rows={3}
                      maxLength={game.config.maxPromptLength}
                      value={draftPrompt}
                      onChange={(e) => setDraftPrompt(e.target.value)}
                    />
                    <div className="mt-1 text-right text-xs text-slate-500">
                      {draftPrompt.length}/{game.config.maxPromptLength}
                    </div>
                    <Button
                      type="button"
                      className="mt-2"
                      disabled={savingPrompt}
                      onClick={() => void handleSavePrompt()}
                    >
                      Save prompt
                    </Button>
                  </div>

                  <div>
                    <h3 className="mb-2 text-sm font-semibold text-slate-200">
                      Secret {game.config.choiceLabel}
                    </h3>
                    <p className="mb-2 text-xs text-slate-500">Only you see your pick until the game finishes.</p>
                    <div className="flex flex-wrap gap-2">
                      {game.config.choices.map((c) => (
                        <button
                          key={c}
                          type="button"
                          disabled={savingChoice}
                          className="min-w-[4.5rem] rounded-lg px-3 py-2 text-sm font-medium shadow ring-1 ring-black/20 transition hover:opacity-90"
                          style={choiceStyle(c)}
                          onClick={() => void handlePickChoice(c)}
                        >
                          {c}
                        </button>
                      ))}
                    </div>
                    {mySlot.choice ? (
                      <p className="mt-2 text-xs text-slate-400">
                        Your pick: <strong>{mySlot.choice}</strong>
                      </p>
                    ) : null}
                  </div>
                </>
              )}
            </div>
          )}
        </div>
      )}

      {resultsData?.results && (
        <div className="mt-10 border-t border-slate-800 pt-8">
          <h2 className="mb-3 text-xl font-semibold">Latest results</h2>
          <div className="rounded-lg border border-slate-700 bg-slate-900/30 p-4 text-sm">
            <p>
              <span className="text-slate-400">Agent declared:</span>{" "}
              <strong className="text-lg text-violet-200">{resultsData.results.agentDeclaredChoice}</strong>
            </p>
            {resultsData.results.agentReasoning ? (
              <p className="mt-2 text-slate-300">{resultsData.results.agentReasoning}</p>
            ) : null}
            <div className="mt-4">
              <h4 className="mb-2 text-xs font-semibold uppercase tracking-wide text-slate-500">Scoring</h4>
              <ul className="space-y-1">
                {(resultsData.results.scoringResults || []).map((r) => (
                  <li key={r.userId} className="flex justify-between gap-2 border-b border-slate-800/80 py-1">
                    <span>
                      @{r.username} <span className="text-slate-500">({r.choice})</span>
                    </span>
                    <span className="text-emerald-300">+{r.respectAwarded} respect</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
